"use client";

import { List, RadioGroupRoot } from "@dafiti/closet-ui";
import { useTranslations } from "next-intl";
import type { PropsWithChildren } from "react";

import type { CategoryChild, CategoryTree } from "../../../types/filters.type";
import { CategoryCheckboxItem, CategoryRadioItem } from "./CategoryItem";

interface CategoryLevelProps {
  tree: CategoryTree;
  localSelection: string[];
  isAllSelected: boolean;
  isCompact?: boolean;
  onNavigate: (child: CategoryChild) => void;
  onToggleItem: (id: string) => void;
  onSelectAll: () => void;
  onSelectRadio: (id: string) => void;
}

function CategoryLevel({
  tree,
  localSelection,
  isAllSelected,
  isCompact,
  onNavigate,
  onToggleItem,
  onSelectAll,
  onSelectRadio,
}: CategoryLevelProps) {
  const t = useTranslations("catalog");

  if (tree.level >= 3) {
    return (
      <CategoryList>
        <CategoryCheckboxItem
          id={`select-all-${tree.id ?? "root"}`}
          label={t("facet.selectAll")}
          checked={isAllSelected}
          isCompact={isCompact}
          onCheckedChange={onSelectAll}
        />
        {(tree.children ?? []).map((child) => (
          <CategoryCheckboxItem
            key={child.id}
            id={child.id}
            label={child.label}
            checked={localSelection.includes(child.id)}
            isCompact={isCompact}
            onCheckedChange={() => onToggleItem(child.id)}
          />
        ))}
      </CategoryList>
    );
  }

  return (
    <RadioGroupRoot
      value={localSelection[0] ?? ""}
      onValueChange={onSelectRadio}
    >
      <CategoryList>
        {(tree.children ?? []).map((child) => (
          <CategoryRadioItem
            key={child.id}
            child={child}
            isCompact={isCompact}
            onNavigate={onNavigate}
          />
        ))}
      </CategoryList>
    </RadioGroupRoot>
  );
}

function CategoryList({ children }: PropsWithChildren) {
  return <List className="divide-y-0">{children}</List>;
}

export { CategoryLevel, CategoryList };
export type { CategoryLevelProps };
